import React, { useState, useEffect, useMemo } from "react";
import Context from "../../Context";
import { Header, OrderStatusBadge } from "../../components/modules";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/base";
import { BiLogIn } from "react-icons/bi";
import { IoReceiptOutline } from "react-icons/io5";
import { FiSearch, FiRefreshCw, FiEye } from "react-icons/fi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import constant from "../../constants";
import { useLocation, useNavigate } from "react-router-dom";
import { useBatchedOrders } from "./useBatchedOrders";

const ITEMS_PER_PAGE = 50;

export const Orders = () => {
  const { configs, db } = Context();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const STATUS_VALUES = new Set(["approved", "paid", "pending", "canceled"]);
  const pathSegment = pathname.split("/").pop();
  const isUnknownChild =
    pathname !== "/orders" && !STATUS_VALUES.has(pathSegment);
  const statusFromPath = STATUS_VALUES.has(pathSegment) ? pathSegment : "";

  useEffect(() => {
    if (isUnknownChild) navigate("/orders", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnknownChild]);

  const [roundDate, setRoundDate] = useState(configs?.roundDate || "");
  const [searchValue, setSearchValue] = useState("");
  const [searchType, setSearchType] = useState("phone");
  const [status, setStatus] = useState(statusFromPath);
  const [appliedSearch, setAppliedSearch] = useState(null);
  const [orderLimit, setOrderLimit] = useState(300);
  const [currentPage, setCurrentPage] = useState(1);

  const { orders, isLive, loading, loadingMore, hasMore, error, loadMore, refresh } =
    useBatchedOrders({
      db,
      roundDate,
      status,
      batchSize: orderLimit,
      search: appliedSearch,
      currentPage,
      pageSize: ITEMS_PER_PAGE,
    });
  const getOrders = () => {
    setCurrentPage(1);
    refresh();
  };

  useEffect(() => {
    if (configs?.roundDate && !roundDate) {
      setRoundDate(configs.roundDate);
    }
  }, [configs?.roundDate, roundDate]);

  useEffect(() => {
    setCurrentPage(1);
    setStatus(statusFromPath);
    setSearchValue("");
    setSearchType("phone");
    setAppliedSearch(null);
  }, [statusFromPath]);

  useEffect(() => {
    setCurrentPage(1);
  }, [roundDate, status, orderLimit, appliedSearch]);

  const handleSearch = (e) => {
    e?.preventDefault();
    const value = searchValue.trim();
    setAppliedSearch(value ? { field: searchType, value } : null);
    getOrders();
  };

  const handleReset = () => {
    setSearchValue("");
    setSearchType("phone");
    setAppliedSearch(null);
    getOrders();
  };

  const totalItems = orders.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE) || 1;

  const paginatedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return orders.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [orders, currentPage]);

  const canGoNext =
    !loading && !loadingMore && (currentPage < totalPages || hasMore);
  const handleNextPage = async () => {
    if (!canGoNext) return;
    const nextPage = currentPage + 1;
    if (currentPage < totalPages || (await loadMore())) {
      setCurrentPage(nextPage);
    }
  };

  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = [];
    if (currentPage <= 4) {
      pages.push(1, 2, 3, 4, 5, "...", totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(
        1,
        "...",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      );
    } else {
      pages.push(
        1,
        "...",
        currentPage - 1,
        currentPage,
        currentPage + 1,
        "...",
        totalPages,
      );
    }
    return pages;
  };

  const statusTabs = [
    {
      value: "",
      label: "ทั้งหมด",
      activeColor: "bg-gray-800 text-white shadow-sm",
      inactiveColor: "bg-gray-100 text-gray-700 hover:bg-gray-200",
    },
    {
      value: constant.statuses.PENDING,
      label: constant.statusesTranslate.pending,
      activeColor: "bg-amber-500 text-white shadow-sm",
      inactiveColor:
        "bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100",
    },
    {
      value: constant.statuses.PAID,
      label: constant.statusesTranslate.paid,
      activeColor: "bg-blue text-white shadow-sm",
      inactiveColor:
        "bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100",
    },
    {
      value: constant.statuses.APPROVE,
      label: constant.statusesTranslate.approved,
      activeColor: "bg-green text-white shadow-sm",
      inactiveColor:
        "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100",
    },
    {
      value: constant.statuses.CANCEL,
      label: constant.statusesTranslate.canceled,
      activeColor: "bg-failed text-white shadow-sm",
      inactiveColor:
        "bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100",
    },
  ];

  return (
    <div className="h-fit w-full max-w-[1600px] mx-auto pb-10 min-w-0">
      <Header
        header="Orders"
        subHeader={
          <div className="flex items-center gap-2">
            <span>
              {`คำสั่งซื้อสลาก${
                statusFromPath
                  ? ` (${constant.statusesTranslate[statusFromPath] || statusFromPath})`
                  : "ทั้งหมด"
              }`}
            </span>
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-rose-50 border border-rose-200 rounded-md text-rose-700 text-[11px] font-bold shadow-xs">
              <span className="relative flex h-2 w-2">
                <span className={`${isLive ? "animate-ping" : ""} absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75`}></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500"></span>
              </span>
              <span>{isLive ? "Realtime" : loading ? "กำลังโหลด" : "ข้อมูล ณ ตอนโหลด"}</span>
            </span>
          </div>
        }
        icon={<IoReceiptOutline className="text-blue" />}
      />

      {/* Control Card: Filters, Status Tabs & Search */}
      <div className="mt-4 bg-white rounded-md shadow-md border border-gray-200 p-4 flex flex-col gap-4">
        {/* Top bar: Status Filter Tabs */}
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 pb-4 w-full shrink-0">
          {statusTabs.map((tab) => {
            const isActive = (status || "") === tab.value;
            return (
              <button
                key={tab.value}
                type="button"
                onClick={() => {
                  setCurrentPage(1);
                  setStatus(tab.value);
                }}
                className={`px-3.5 py-1.5 text-sm font-bold rounded-md transition-all duration-150 cursor-pointer ${
                  isActive ? tab.activeColor : tab.inactiveColor
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Filter Form: RoundDate & Search Fields */}
        <form
          onSubmit={handleSearch}
          className="flex flex-wrap items-end gap-3 w-full shrink-0"
        >
          {/* RoundDate Selector */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-gray-700">งวดสลาก</label>
            <select
              className="h-10 min-w-[140px] px-3 bg-white border border-gray-300 text-sm font-semibold rounded-md shadow-xs focus:outline-none focus:border-blue transition-all"
              onChange={(e) => {
                setRoundDate(e.target.value);
                setCurrentPage(1);
              }}
              value={roundDate || ""}
            >
              {configs?.roundDates?.map((date, index) => (
                <option key={index} value={date}>
                  {date}
                </option>
              ))}
            </select>
          </div>

          {/* Search Type Selector */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-gray-700">ค้นหาตาม</label>
            <select
              className="h-10 min-w-[140px] px-3 bg-white border border-gray-300 text-sm font-semibold rounded-md shadow-xs focus:outline-none focus:border-blue transition-all cursor-pointer"
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
            >
              <option value="shortId">รหัสคำสั่งซื้อ</option>
              <option value="phone">เบอร์โทรศัพท์</option>
              <option value="userId">รหัสผู้ใช้</option>
              <option value="firstName">ชื่อ</option>
              <option value="lastName">นามสกุล</option>
              <option value="lineTitle">ชื่อไลน์</option>
              <option value="lineId">ไลน์ไอดี</option>
            </select>
          </div>

          {/* Search Input Box */}
          <div className="flex flex-col gap-1 flex-1 min-w-[200px] max-w-[420px]">
            <label className="text-xs font-bold text-gray-700">คำค้นหา</label>
            <input
              className="h-10 w-full px-3 bg-white border border-gray-300 text-sm font-semibold rounded-md shadow-xs focus:outline-none focus:border-blue transition-all placeholder:text-gray-400"
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="พิมพ์เพื่อค้นหา..."
            />
          </div>

          {/* Limit Selector */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-gray-700">
              โหลดเพิ่มครั้งละ
            </label>
            <select
              className="h-10 min-w-[110px] px-3 bg-white border border-gray-300 text-sm font-semibold rounded-md shadow-xs focus:outline-none focus:border-blue transition-all cursor-pointer"
              value={orderLimit}
              onChange={(e) => {
                setOrderLimit(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="100">100</option>
              <option value="200">200</option>
              <option value="300">300</option>
              <option value="500">500</option>
              <option value="1000">1,000</option>
            </select>
          </div>

          {/* Action Buttons: Search & Refresh */}
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={loading}
              className="h-10 px-4 flex items-center justify-center gap-1.5 bg-blue hover:bg-blue/90 text-white text-sm font-bold rounded-md shadow-xs transition-all duration-150 disabled:opacity-50 cursor-pointer"
            >
              <FiSearch className="w-4 h-4" />
              <span>ค้นหา</span>
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={handleReset}
              title="รีเซ็ตและรีเฟรชข้อมูล"
              className="h-10 px-4 flex items-center justify-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-bold rounded-md border border-gray-300 shadow-xs transition-all cursor-pointer"
            >
              <FiRefreshCw
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
              <span>รีเซ็ต</span>
            </button>
          </div>
        </form>
      </div>

      {/* Orders Table Card */}
      <div className="mt-4 bg-white w-full rounded-md shadow-md border border-gray-200 overflow-hidden">
        {/* Table Title & Pagination Bar */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50/60 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-gray-800">
              รายการคำสั่งซื้อ
              {status
                ? ` (${constant.statusesTranslate[status] || status})`
                : ""}
            </h2>
          </div>

          <button type="button" onClick={getOrders} disabled={loading}
            className="text-sm font-semibold text-blue disabled:opacity-50">
            โหลดรายการล่าสุด
          </button>

          {(totalPages > 1 || hasMore) && (
            <div>
              <Pagination className="mx-0">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      className={`${
                        currentPage > 1
                          ? "cursor-pointer"
                          : "cursor-not-allowed hover:bg-transparent opacity-45 pointer-events-none"
                      } font-semibold`}
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                    />
                  </PaginationItem>

                  {getPageNumbers().map((p, idx) => (
                    <PaginationItem key={idx}>
                      {p === "..." ? (
                        <span className="px-2 text-gray-400 text-sm">...</span>
                      ) : (
                        <PaginationLink
                          onClick={() => setCurrentPage(p)}
                          isActive={currentPage === p}
                          className="cursor-pointer font-semibold rounded-md"
                        >
                          {p}
                        </PaginationLink>
                      )}
                    </PaginationItem>
                  ))}

                  <PaginationItem>
                    <PaginationNext
                      className={`${
                        canGoNext
                          ? "cursor-pointer"
                          : "cursor-not-allowed hover:bg-transparent opacity-45 pointer-events-none"
                      } font-semibold`}
                      onClick={handleNextPage}
                      aria-disabled={!canGoNext}
                      aria-label={
                        loadingMore ? "กำลังโหลดรายการถัดไป" : "Go to next page"
                      }
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>

        {error && (
          <p role="alert" className="px-4 py-2 text-sm text-rose-600">
            {error}
          </p>
        )}

        {/* Orders Table Container with Horizontal Scroll */}
        <div className="w-full overflow-x-auto">
          <Table>
            <TableHeader className="bg-gray-100/90 border-b border-gray-200">
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-center font-bold text-gray-700 py-3 w-12 whitespace-nowrap">
                  ลำดับ
                </TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">
                  <span>เวลาสั่งซื้อ</span>
                  <span className="block text-[11px] text-rose-500 font-normal">
                    (วันหมดอายุ)
                  </span>
                </TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">
                  รหัสคำสั่งซื้อ
                </TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">
                  รหัสผู้ใช้
                </TableHead>
                <TableHead className="font-bold text-gray-700 py-3 whitespace-nowrap">
                  เบอร์โทรศัพท์
                </TableHead>
                <TableHead className="font-bold text-gray-700 py-3 whitespace-nowrap">
                  ชื่อ-นามสกุล
                </TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">
                  จำนวน
                </TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">
                  ยอดรวม
                </TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">
                  ช่องทาง
                </TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">
                  สลิป
                </TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">
                  สถานะ
                </TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 w-20 whitespace-nowrap">
                  จัดการ
                </TableHead>
                <TableHead
                  className="text-center font-bold text-gray-700 py-3 whitespace-nowrap w-24"
                  title="แอดมินดูคำสั่งซื้อนี้แล้ว"
                >
                  <div className="flex justify-center items-center">
                    <FiEye className="w-4 h-4 text-gray-600" />
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan="13" className="h-72 text-center">
                    <div className="flex flex-col justify-center items-center gap-3 text-gray-500">
                      <div className="w-8 h-8 border-4 border-blue border-t-transparent rounded-full animate-spin" />
                      <p className="text-sm font-semibold">
                        กำลังโหลดข้อมูลคำสั่งซื้อ...
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan="13"
                    className="h-72 text-center text-gray-400 font-semibold"
                  >
                    <div className="flex flex-col items-center justify-center gap-2">
                      <IoReceiptOutline className="w-12 h-12 text-gray-300" />
                      <p className="text-base">
                        ไม่พบข้อมูลคำสั่งซื้อในเงื่อนไขที่เลือก
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedOrders.map((order, index) => (
                  <OrderCell
                    order={order}
                    key={order.id || index}
                    index={(currentPage - 1) * ITEMS_PER_PAGE + index}
                    getOrders={getOrders}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Bottom Pagination Bar */}
        {(totalPages > 1 || hasMore) && (
          <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50/60 flex-wrap gap-3">
            <div className="text-xs font-semibold text-gray-500">
              แสดง {orders.length ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0}{" "}
              - {Math.min(currentPage * ITEMS_PER_PAGE, orders.length)} จาก{" "}
              {orders.length.toLocaleString()} รายการที่โหลดแล้ว (หน้า{" "}
              {currentPage} จาก {totalPages} หน้าที่โหลดแล้ว)
            </div>
            <Pagination className="mx-0 w-auto">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    className={`${
                      currentPage > 1
                        ? "cursor-pointer"
                        : "cursor-not-allowed hover:bg-transparent opacity-45 pointer-events-none"
                    } font-semibold`}
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                  />
                </PaginationItem>

                {getPageNumbers().map((p, idx) => (
                  <PaginationItem key={idx}>
                    {p === "..." ? (
                      <span className="px-2 text-gray-400 text-sm">...</span>
                    ) : (
                      <PaginationLink
                        onClick={() => setCurrentPage(p)}
                        isActive={currentPage === p}
                        className="cursor-pointer font-semibold rounded-md"
                      >
                        {p}
                      </PaginationLink>
                    )}
                  </PaginationItem>
                ))}

                <PaginationItem>
                  <PaginationNext
                    className={`${
                      canGoNext
                        ? "cursor-pointer"
                        : "cursor-not-allowed hover:bg-transparent opacity-45 pointer-events-none"
                    } font-semibold`}
                    onClick={handleNextPage}
                    aria-disabled={!canGoNext}
                    aria-label={
                      loadingMore ? "กำลังโหลดรายการถัดไป" : "Go to next page"
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>
    </div>
  );
};

const getWorkingAdmins = (order) => {
  const workingField = order?.working || order?.workingAdmin;
  if (!workingField) return [];

  const list = Array.isArray(workingField) ? workingField : [workingField];
  const result = [];
  const seen = new Set();

  list.forEach((item) => {
    if (!item) return;
    if (typeof item === "string") {
      if (!seen.has(item)) {
        seen.add(item);
        result.push({
          id: item,
          name: item,
          firstName: item,
          profileImage: "",
        });
      }
    } else if (typeof item === "object") {
      const idKey = String(
        item.id ||
          item.userId ||
          item.username ||
          item.name ||
          item.firstName ||
          "",
      );
      if (idKey && !seen.has(idKey)) {
        seen.add(idKey);
        const name =
          item.name || item.displayName || item.firstName || "แอดมิน";
        const firstName = item.firstName || item.displayName || name;
        result.push({
          id: idKey,
          name,
          firstName,
          profileImage: item.profileImage || "",
        });
      }
    }
  });

  return result;
};

const OrderCell = React.memo(({ order, index }) => {
  const { callApi } = Context();
  const [loading, setLoading] = useState(false);
  const workingAdmins = getWorkingAdmins(order);

  const formatDateTime = (timestamp) => {
    if (!timestamp) return "-";
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return "-";
    return (
      d.toLocaleDateString("th-TH", {
        year: "2-digit",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }) + " น."
    );
  };

  return (
    <TableRow
      className="h-[72px] cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100"
      onClick={() => {
        if (window.getSelection()?.toString()) return;
        window.open(`/orders/${order.id}`, "_blank");
      }}
    >
      {/* Index */}
      <TableCell className="text-center font-semibold text-gray-500 text-xs whitespace-nowrap">
        {index + 1}
      </TableCell>

      {/* Date & Expire */}
      <TableCell className="text-center whitespace-nowrap">
        <div className="text-xs font-semibold text-gray-700">
          {formatDateTime(order.createdAt)}
        </div>
        {order.expireAt && (
          <div className="text-failed text-[11px] font-semibold mt-0.5">
            ({formatDateTime(order.expireAt)})
          </div>
        )}
      </TableCell>

      {/* Short ID */}
      <TableCell className="text-center font-bold font-mono text-blue text-sm whitespace-nowrap">
        {order.shortId || "-"}
      </TableCell>

      {/* User ID */}
      <TableCell className="text-center font-mono text-xs text-gray-600 font-semibold whitespace-nowrap">
        {order.userId || "-"}
      </TableCell>

      {/* Phone */}
      <TableCell className="font-semibold text-gray-700 text-xs font-mono whitespace-nowrap">
        {order.phone || "-"}
      </TableCell>

      {/* Customer Name */}
      <TableCell className="font-semibold text-gray-800 text-sm whitespace-nowrap">
        {`${order.firstName || ""} ${order.lastName || ""}`.trim() || "-"}
      </TableCell>

      {/* Quantity */}
      <TableCell className="text-center font-bold font-mono text-gray-700 text-sm whitespace-nowrap">
        {order.size?.toLocaleString() || 0}
      </TableCell>

      {/* Price */}
      <TableCell className="text-center font-bold font-mono text-gray-900 text-sm whitespace-nowrap">
        {order.paidCurrency === "LAK"
          ? `${order.totalPrice_lak?.toLocaleString()} กีบ`
          : `${order.totalPrice?.toLocaleString()} ฿`}
      </TableCell>

      {/* Payment Channel */}
      <TableCell className="text-center whitespace-nowrap">
        {order.paymentChannel === "bank" ? (
          <img
            src="/images/banktransfer.png"
            className="mx-auto h-6 object-contain"
            alt="Bank Transfer"
          />
        ) : order.paymentChannel === "qr" ? (
          <img
            src="/images/qr.png"
            className="mx-auto h-6 object-contain"
            alt="QR PromptPay"
          />
        ) : (
          <span className="text-xs text-gray-400 font-semibold">-</span>
        )}
      </TableCell>

      {/* Slip Preview */}
      <TableCell className="text-center whitespace-nowrap">
        {order.slips ? (
          <div className="flex justify-center">
            <img
              onClick={(e) => {
                e.stopPropagation();
                window.open(order.slips, "_blank");
              }}
              src={order.slips}
              className="w-10 h-10 object-cover rounded-md border border-gray-200 hover:scale-110 shadow-xs transition-transform cursor-pointer"
              alt="Slip"
              title="คลิกเพื่อดูสลิปขนาดเต็ม"
            />
          </div>
        ) : (
          <span className="text-xs text-gray-400 font-semibold">-</span>
        )}
      </TableCell>

      {/* Status Badge */}
      <TableCell className="text-center whitespace-nowrap">
        <OrderStatusBadge status={order.status} size="sm" />
      </TableCell>

      {/* Actions */}
      <TableCell
        className="text-center whitespace-nowrap"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          title="เข้าสู่ระบบแทนลูกค้า (Login as User)"
          disabled={loading || !order.userId}
          className="inline-flex items-center justify-center w-8 h-8 bg-blue text-white rounded-md shadow-xs hover:opacity-85 transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
          onClick={async () => {
            setLoading(true);
            try {
              const result = await callApi("users-addAdminToken", {
                userId: order.userId,
              });
              if (result?.adminToken) {
                window.open(
                  constant.getAdminOrderUrl(result.adminToken),
                  "_blank",
                );
              }
            } catch (err) {
              console.error("Login as user error:", err);
            } finally {
              setLoading(false);
            }
          }}
        >
          {loading ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <BiLogIn className="w-4 h-4" />
          )}
        </button>
      </TableCell>

      {/* แอดมินผู้รับผิดชอบ/เปิดดูออเดอร์ (รูปโปรไฟล์ซ้อนกัน + แสดงชื่อทันทีเมื่อชี้) */}
      <TableCell
        className="text-center whitespace-nowrap"
        onClick={(e) => e.stopPropagation()}
      >
        {workingAdmins.length > 0 ? (
          <div className="flex items-center justify-center -space-x-2 py-1">
            {workingAdmins.map((admin, idx) => {
              const displayName = admin.name || admin.firstName || "แอดมิน";
              return (
                <div
                  key={admin.id || idx}
                  className="relative group inline-block shrink-0 cursor-pointer"
                >
                  <div className="relative w-7 h-7 rounded-full ring-2 ring-white shadow-xs overflow-hidden bg-slate-100 text-slate-700 transition-transform group-hover:scale-110 group-hover:z-30">
                    {admin.profileImage ? (
                      <img
                        src={admin.profileImage}
                        alt={displayName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[11px] font-semibold bg-slate-200 text-slate-700">
                        {(admin.firstName || admin.name || "A")
                          .slice(0, 1)
                          .toUpperCase()}
                      </div>
                    )}
                  </div>

                  {/* Minimal Floating Tooltip */}
                  <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:flex flex-col items-center z-50">
                    <div className="bg-slate-900 text-white text-[11px] font-medium px-2 py-0.5 rounded-md shadow-md whitespace-nowrap">
                      {displayName}
                    </div>
                    <div className="w-1.5 h-1.5 bg-slate-900 rotate-45 -mt-0.5"></div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <span className="text-xs text-gray-400 font-semibold">-</span>
        )}
      </TableCell>
    </TableRow>
  );
});

export default Orders;
